import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Building2, ShieldCheck, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function SignupScreen() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [portfolioName, setPortfolioName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [seedDemo, setSeedDemo] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedPort = portfolioName.trim();
    const trimmedUser = username.trim();

    if (!trimmedPort) {
      toast.error("Please enter a portfolio or company name");
      return;
    }
    if (trimmedPort.length < 2) {
      toast.error("Portfolio name must be at least 2 characters");
      return;
    }
    if (!trimmedUser) {
      toast.error("Please enter a username");
      return;
    }
    if (trimmedUser.length < 3) {
      toast.error("Username must be at least 3 characters");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }

    setBusy(true);
    try {
      await signup({
        portfolioName: trimmedPort,
        username: trimmedUser,
        email: email.trim() || null,
        password,
        seedDemo,
      });
      toast.success("Portfolio created successfully!");
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8 bg-gradient-to-br from-[#F5F5F7] via-white to-[#EDF1F7]"
      data-testid="signup-screen"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-[#1C3A5E] items-center justify-center mb-3 shadow-lg shadow-[#1C3A5E]/20">
            <Building2 className="text-white" size={26} />
          </div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#86868B] font-semibold">
            Dedicated Account Provisioning
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 text-[#1D1D1F]">
            Create your portfolio
          </h1>
          <p className="text-sm text-[#86868B] mt-1.5 max-w-sm mx-auto">
            Get an isolated database with private storage for your rental properties and tax compliance.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="card p-6 sm:p-8 space-y-4 shadow-xl border border-black/5 bg-white/90 backdrop-blur-sm"
          data-testid="signup-form"
        >
          <div>
            <label className="label">Portfolio or Company Name</label>
            <input
              type="text"
              data-testid="signup-portfolio-input"
              className="lapen-input"
              placeholder="e.g. Cascade Crest Capital LLC"
              value={portfolioName}
              onChange={(e) => setPortfolioName(e.target.value)}
              required
              autoFocus
            />
            <p className="text-[11px] text-[#86868B] mt-1">
              Used to name your portfolio entity and reports.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Username</label>
              <input
                type="text"
                data-testid="signup-username-input"
                className="lapen-input"
                placeholder="e.g. alex_investor"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
              />
            </div>
            <div>
              <label className="label">Email (optional)</label>
              <input
                type="email"
                data-testid="signup-email-input"
                className="lapen-input"
                placeholder="alex@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Password</label>
              <input
                type="password"
                data-testid="signup-password-input"
                className="lapen-input"
                placeholder="Min. 6 chars"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <div>
              <label className="label">Confirm password</label>
              <input
                type="password"
                data-testid="signup-confirm-input"
                className="lapen-input"
                placeholder="Re-enter password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
          </div>

          {/* Demo Data Option */}
          <div className="pt-1">
            <label className="flex items-start gap-3 p-3 rounded-xl border border-[var(--border)] bg-[#F8FAFC] hover:bg-[#F1F5F9] cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={seedDemo}
                onChange={(e) => setSeedDemo(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#1C3A5E] focus:ring-[#1C3A5E]"
              />
              <div className="text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[var(--text)] flex items-center gap-1.5">
                    <Sparkles size={13} className="text-[#3B82F6]" />
                    Load sample demo properties & transactions
                  </span>
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">
                    Optional
                  </span>
                </div>
                <p className="text-[var(--muted)] mt-1 text-[11px] leading-relaxed">
                  <strong>Leave unchecked (recommended)</strong> to start with a 100% clean, blank portfolio (0 properties). If checked, sample duplex/cottage properties are loaded.
                </p>
              </div>
            </label>
          </div>

          {/* Security & Isolation Pill */}
          <div className="rounded-lg bg-emerald-50 border border-emerald-200/60 p-2.5 flex items-center gap-2 text-emerald-800 text-[11px]">
            <ShieldCheck size={16} className="text-emerald-600 shrink-0" />
            <span>
              <strong>Zero Cross-Account Impact:</strong> Created inside a dedicated, isolated SQLite database file.
            </span>
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="signup-submit-button"
            className="btn-primary w-full py-2.5 text-sm font-semibold tracking-wide flex items-center justify-center gap-2"
          >
            {busy ? (
              "Provisioning database…"
            ) : (
              <>
                <CheckCircle2 size={16} /> Create Portfolio & Account
              </>
            )}
          </button>

          <div className="text-center pt-2">
            <p className="text-xs text-[#86868B]">
              Already have an account?{" "}
              <Link
                to="/login"
                className="font-semibold text-[#1C3A5E] hover:underline"
              >
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
